import React, { useState } from 'react';
import {
  Container,
  Typography,
  Box,
  TextField,
  Button,
  Paper,
  Alert,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { projectService } from '../../services/projectService';

const ProjectForm = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    project_code: '',
    name: '',
    baseline_burn_value: '',
    baseline_month: '',
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    // Validation
    if (!formData.project_code || !formData.name) {
      setError('Please fill in all required fields');
      return;
    }

    try {
      setLoading(true);

      // Format baseline month to YYYY-MM-01 if provided
      const formattedBaselineMonth = formData.baseline_month
        ? `${formData.baseline_month}-01`
        : null;

      // Parse baseline burn value
      const baselineBurnValue = formData.baseline_burn_value && formData.baseline_burn_value !== ''
        ? parseFloat(formData.baseline_burn_value)
        : 0;

      // Create project
      await projectService.create({
        project_code: formData.project_code,
        name: formData.name,
        baseline_burn_value: baselineBurnValue,
        baseline_month: formattedBaselineMonth,
      });

      setSuccess('Project created successfully!');

      // Redirect to project list after 1.5 seconds
      setTimeout(() => {
        navigate('/projects');
      }, 1500);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create project');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/projects');
  };

  return (
    <Container maxWidth="md">
      <Box sx={{ mt: 4, mb: 4 }}>
        <Typography variant="h4" gutterBottom>
          Add New Project
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {success}
          </Alert>
        )}

        <Paper sx={{ p: 4 }}>
          <form onSubmit={handleSubmit}>
            <TextField
              fullWidth
              label="Project Code"
              name="project_code"
              value={formData.project_code}
              onChange={handleChange}
              required
              margin="normal"
              placeholder="e.g., PROJ-001"
              helperText="Unique project identifier"
            />

            <TextField
              fullWidth
              label="Project Name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
              margin="normal"
              placeholder="e.g., Mobile App Development"
            />

            <TextField
              fullWidth
              label="Baseline Burn Value (Optional)"
              name="baseline_burn_value"
              type="number"
              value={formData.baseline_burn_value}
              onChange={handleChange}
              margin="normal"
              placeholder="e.g., 50000"
              helperText="Cumulative burn value from project start till baseline month (leave empty if none)"
            />

            <TextField
              fullWidth
              label="Baseline Month (Optional)"
              name="baseline_month"
              type="month"
              value={formData.baseline_month}
              onChange={handleChange}
              margin="normal"
              InputLabelProps={{ shrink: true }}
              helperText="Month till which baseline burn value is calculated (billing can only be generated after this)"
            />

            <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={loading}
                fullWidth
              >
                {loading ? 'Creating...' : 'Create Project'}
              </Button>
              <Button
                variant="outlined"
                color="secondary"
                onClick={handleCancel}
                disabled={loading}
                fullWidth
              >
                Cancel
              </Button>
            </Box>
          </form>
        </Paper>
      </Box>
    </Container>
  );
};

export default ProjectForm;
